import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Brand, { clampBrandLogoHeight, BRAND_LOGO_HEADER_DEFAULT, BRAND_LOGO_FOOTER_DEFAULT, BRAND_LOGO_MOBILE_DEFAULT } from "@/components/Brand";
import { POLICIES } from "@/lib/policies";
import { platformDisplayHost } from "@/lib/api";
import { Menu, X, ArrowRight } from "lucide-react";

export const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export const DEFAULT_YEARLY_PLANS = [
  {
    id: "website",
    name: "Website",
    price: 1999,
    period: "year",
    tagline: "Your dukaan on the internet — GST, UPI, COD included.",
    features: ["Cart, checkout & free SSL", "Razorpay, Cashfree, WhatsApp & COD", "SEO, coupons, wishlist", "Up to 100 products"],
    cta_text: "Start my store",
    footnote: "Add your own Flutter app anytime from your dashboard.",
  },
  {
    id: "website_app",
    name: "Website + Flutter App",
    price: 5998,
    period: "year",
    badge: "Best value",
    tagline: "Website plus your own Android app, built on our servers.",
    features: ["Everything in the Website plan", "Branded Flutter app", "Play Store approval support", "Same catalog, orders & payments"],
    cta_text: "Get website + app",
    footnote: "Website ₹1,999 + Flutter App ₹3,999 — bundled.",
    highlight: true,
  },
  {
    id: "website_app_ios",
    name: "iOS + Android + Website",
    price: 19999,
    period: "year",
    badge: "Full stack",
    tagline: "Website plus Android and iOS. You publish iOS with your own Apple account.",
    features: ["Everything in Website + Android", "iOS builder in your dashboard", "Codemagic IPA build", "Your Apple Developer account"],
    cta_text: "Get website + iOS + Android",
    footnote: "Apple’s $99/year developer fee is paid by you to Apple.",
  },
];

export function yearlyPlans(plans) {
  const list = Array.isArray(plans) ? plans : [];
  const yearly = list.filter((p) => {
    const per = String(p?.period || "year").toLowerCase().trim();
    return !["month", "mo", "monthly", "m"].includes(per);
  });
  return yearly.length ? yearly : DEFAULT_YEARLY_PLANS;
}

export function landingLogoVars(cfg) {
  return {
    "--bh": `${clampBrandLogoHeight(cfg?.brand?.logo_height_header, BRAND_LOGO_HEADER_DEFAULT)}px`,
    "--bf": `${clampBrandLogoHeight(cfg?.brand?.logo_height_footer, BRAND_LOGO_FOOTER_DEFAULT)}px`,
    "--bm": `${clampBrandLogoHeight(cfg?.brand?.logo_height_mobile, BRAND_LOGO_MOBILE_DEFAULT)}px`,
  };
}

export function LandingHeader({ websitePrice, active = "" }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const themesClass = active === "themes" ? "text-stone-900" : "hover:text-stone-900";
  const featuresClass = active === "features" ? "text-stone-900" : "hover:text-stone-900";
  const pricingClass = active === "pricing" ? "text-stone-900" : "hover:text-stone-900";
  return (
    <header className="sticky top-0 z-50 border-b border-black/[0.06] ms-glass">
      <div className="max-w-[1280px] mx-auto px-5 lg:px-8 ms-site-header flex items-center justify-between gap-3 py-2">
        <Link to="/" data-testid="landing-brand" className="min-w-0 shrink"><Brand variant="light" size="xl" placement="header" /></Link>
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-stone-600 ms-menu">
          <a href="/#app" className="hover:text-stone-900">Ecom App</a>
          <Link to="/blog" className="hover:text-stone-900" data-testid="landing-blog-link">Blog</Link>
          <Link to="/features" className={featuresClass} data-testid="landing-features-link">Features</Link>
          <Link to="/theme" className={themesClass} data-testid="landing-themes-link">Themes</Link>
          <Link to="/pricing" className={pricingClass} data-testid="landing-pricing-link">Pricing</Link>
        </nav>
        <div className="hidden md:flex items-center gap-2.5">
          <Link to="/login" className="text-sm font-medium text-stone-700 hover:text-stone-900 px-2" data-testid="landing-login-link">Sign in</Link>
          <Link to="/signup?plan=website" data-testid="landing-cta-signup">
            <Button className="bg-stone-900 hover:bg-stone-800 text-white font-semibold rounded-full px-5">Start Selling — {rupee(websitePrice)}</Button>
          </Link>
        </div>
        <button
          type="button"
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl border border-black/10 bg-white"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
          data-testid="mobile-menu-toggle"
        >
          {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {menuOpen && (
        <div className="md:hidden border-t border-black/[0.06] bg-[#FFFBF2]" data-testid="mobile-menu-panel">
          <nav className="max-w-[1280px] mx-auto px-5 py-4 flex flex-col gap-1 text-base font-medium text-stone-700 ms-menu">
            <a href="/#app" onClick={() => setMenuOpen(false)} className="py-2.5 border-b border-black/5">Ecom App</a>
            <Link to="/blog" onClick={() => setMenuOpen(false)} className="py-2.5 border-b border-black/5">Blog</Link>
            <Link to="/features" onClick={() => setMenuOpen(false)} className="py-2.5 border-b border-black/5">Features</Link>
            <Link to="/theme" onClick={() => setMenuOpen(false)} className="py-2.5 border-b border-black/5" data-testid="mobile-themes-link">Themes</Link>
            <Link to="/pricing" onClick={() => setMenuOpen(false)} className="py-2.5 border-b border-black/5">Pricing</Link>
            <div className="flex items-center gap-3 pt-4">
              <Link to="/login" onClick={() => setMenuOpen(false)} className="flex-1 text-center py-2.5 rounded-full border border-black bg-white font-semibold" data-testid="mobile-menu-login">Sign in</Link>
              <Link to="/signup?plan=website" onClick={() => setMenuOpen(false)} className="flex-1" data-testid="mobile-menu-signup">
                <Button className="w-full bg-stone-900 hover:bg-stone-800 text-white font-semibold rounded-full">Get Started</Button>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

export function LandingPricing({ pricing, plans }) {
  const list = plans || yearlyPlans(pricing?.plans);
  return (
    <section id="pricing" className="relative max-w-[1100px] mx-auto px-5 lg:px-8 py-20">
      <div className="text-center max-w-2xl mx-auto">
        <span className="inline-flex px-3 py-1 rounded-full bg-[#FFEAD1] text-[11px] font-bold">SIMPLE PRICING</span>
        <h2 className="ms-serif mt-4 text-3xl sm:text-4xl font-bold">{pricing?.heading || "Simple pricing. No dollar traps."}</h2>
        {pricing?.subheading && <p className="mt-3 text-stone-600">{pricing.subheading}</p>}
        <p className="mt-2 text-sm font-semibold text-stone-500">Yearly plans only — no monthly billing.</p>
      </div>
      <div className={`mt-12 grid gap-6 items-start ${list.length >= 3 ? "md:grid-cols-3" : list.length > 1 ? "md:grid-cols-2" : "max-w-md mx-auto"}`}>
        {list.map((p, i) => {
          const highlight = p.highlight;
          return (
            <div
              key={p.id || i}
              data-testid={`pricing-plan-${p.id || i}`}
              className={highlight ? "relative rounded-[24px] p-[1.5px] overflow-hidden shadow-lg" : "relative rounded-[24px] bg-white border border-black/10 p-7 lg:p-8 shadow-sm"}
            >
              {highlight ? <div className="absolute inset-0 ms-aurora" /> : null}
              <div className={highlight ? "relative rounded-[22px] bg-[#0F0F0F] text-white p-7 lg:p-8" : ""}>
                <div className="flex justify-between items-start gap-3">
                  <div>
                    {p.badge ? (
                      <span className={`inline-flex px-3 py-1 rounded-full text-[11px] font-extrabold ${highlight ? "bg-white text-black" : "bg-[#FFFBF2] border border-black/10"}`}>{p.badge}</span>
                    ) : null}
                    <h3 className="ms-serif mt-3 text-2xl font-bold">{p.name}</h3>
                    <p className={`mt-1 text-sm ${highlight ? "text-white/70" : "text-stone-600"}`}>{p.tagline}</p>
                  </div>
                </div>
                <div className="mt-5 flex items-end gap-1">
                  <span className="ms-serif text-5xl font-bold font-mono-nums">{rupee(p.price)}</span>
                  <span className={`mb-1.5 text-sm ${highlight ? "text-white/50" : "text-stone-500"}`}>/year</span>
                </div>
                <Link to={`/signup?plan=${p.id || "website"}`} data-testid={`pricing-cta-${p.id || i}`}>
                  <Button className={`mt-6 w-full h-12 rounded-full font-bold ${highlight ? "bg-white text-black hover:bg-stone-100" : "bg-stone-900 hover:bg-stone-800 text-white"}`}>
                    {p.cta_text || "Get started"} <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
                <ul className="mt-7 space-y-3">
                  {(p.features || []).map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5 ${highlight ? "bg-[#2ED4A6] text-black" : "bg-[#E8FFF6] text-emerald-800"}`}>✓</span>
                      <span className={highlight ? "text-stone-100" : "text-stone-700"}>{f}</span>
                    </li>
                  ))}
                </ul>
                {p.footnote ? <p className={`mt-6 text-xs ${highlight ? "text-white/50" : "text-stone-500"}`}>{p.footnote}</p> : null}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function LandingFooter() {
  const host = platformDisplayHost() || "merastartup.in";
  return (
    <footer className="border-t border-black/10 bg-white">
      <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8 text-sm">
          <div>
            <Brand variant="light" size="sm" placement="footer" />
            <p className="mt-3 text-stone-500 text-sm max-w-[28ch]">India&apos;s best alternative to Shopify. Specially designed for Indian startups.</p>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-stone-400">Company</div>
            <div className="mt-3 space-y-2 text-stone-600">
              <Link to="/about" className="block hover:text-stone-900">About</Link>
              <Link to="/company/founder" className="block hover:text-stone-900" data-testid="landing-footer-founder">Founder</Link>
              <Link to="/company/co-founder" className="block hover:text-stone-900" data-testid="landing-footer-cofounder">Co-founder</Link>
            </div>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-stone-400">Product</div>
            <div className="mt-3 space-y-2 text-stone-600">
              <Link to="/theme" className="block hover:text-stone-900">Themes</Link>
              <Link to="/features" className="block hover:text-stone-900">Features</Link>
              <Link to="/pricing" className="block hover:text-stone-900">Pricing</Link>
              <a href="/#app" className="block hover:text-stone-900">Android App</a>
            </div>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-stone-400">Legal</div>
            <div className="mt-3 space-y-2 text-stone-600">
              {POLICIES.map((p) => (
                <Link key={p.key} to={p.path} className="block hover:text-stone-900" data-testid={`landing-footer-${p.key}`}>{p.label}</Link>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-stone-400">Support</div>
            <div className="mt-3 space-y-2 text-stone-600">
              <Link to="/blog" className="block hover:text-stone-900">Blog</Link>
              <Link to="/login" className="block hover:text-stone-900">Sign in</Link>
              <Link to="/contact" className="block hover:text-stone-900">Contact</Link>
            </div>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-black/5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-stone-500">
          <p>© {new Date().getFullYear()} Mera Startup · UPI · COD · Razorpay · Shiprocket</p>
          <Link to="/super-admin/login" className="hover:text-stone-900">Admin</Link>
        </div>
        <p className="sr-only">{host}</p>
      </div>
    </footer>
  );
}

export function LandingStickyCta({ websitePrice }) {
  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-black/10 bg-[#FFFBF2]/95 backdrop-blur px-4 py-2.5 flex items-center justify-between gap-3">
      <div className="leading-tight">
        <div className="text-[11px] text-stone-500">Launch your store</div>
        <div className="font-bold text-stone-900 text-sm">Just {rupee(websitePrice)}/year</div>
      </div>
      <Link to="/signup?plan=website" className="flex-shrink-0" data-testid="sticky-cta">
        <Button className="bg-[#FF8A2B] hover:bg-[#f47a18] text-white font-bold rounded-full px-5">Get Started</Button>
      </Link>
    </div>
  );
}
